import React, { useState, useEffect } from 'react';
import { teamsAPI, usersAPI, teamMembersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Users, LayoutGrid, List, Search, Edit, Trash2, X } from 'lucide-react';

const Teams = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    members: [],
    lead: '',
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [teamsRes, techRes, membersRes] = await Promise.all([
        teamsAPI.getAll(), 
        usersAPI.getTechnicians(),
        teamMembersAPI.getAll()
      ]);
      setTeams(teamsRes.data);
      setTechnicians(techRes.data);
      setTeamMembers(membersRes.data);
    } catch (error) {
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format member display as "Role - Name"
  const formatMemberDisplay = (memberId) => {
    // First check team_members
    const member = teamMembers.find(m => m.id === memberId);
    if (member) {
      const role = member.role ? member.role.charAt(0).toUpperCase() + member.role.slice(1) : 'Worker';
      return `${role} - ${member.name}`;
    }
    // Then check technicians (users)
    const tech = technicians.find(t => t.id === memberId);
    if (tech) {
      const role = tech.role ? tech.role.charAt(0).toUpperCase() + tech.role.slice(1) : 'Technician';
      return `${role} - ${tech.name}`;
    }
    // If still not found, show shortened ID with warning
    return `Member ID: ${memberId?.slice(0, 8) || 'Unknown'}...`;
  };

  // Get member object for display
  const getMemberInfo = (memberId) => {
    const member = teamMembers.find(m => m.id === memberId);
    if (member) return member;
    return technicians.find(t => t.id === memberId);
  };

  const filteredTeams = teams.filter(team => 
    team.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({ name: '', members: [], lead: '' });
    setSelectedTeam(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (team) => {
    setSelectedTeam(team);
    setFormData({
      name: team.name || '',
      members: team.members || [],
      lead: team.lead || '',
    });
    setShowModal(true);
  };

  const openDeleteModal = (team) => {
    setSelectedTeam(team);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTeam) {
        await teamsAPI.update(selectedTeam.id, formData);
        toast.success('Team updated successfully');
      } else {
        await teamsAPI.create(formData);
        toast.success('Team created successfully');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(selectedTeam ? 'Failed to update team' : 'Failed to create team');
    }
  };

  const handleDelete = async () => {
    try {
      await teamsAPI.delete(selectedTeam.id);
      toast.success('Team deleted successfully');
      setShowDeleteModal(false);
      setSelectedTeam(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const handleMemberToggle = (techId) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.includes(techId)
        ? prev.members.filter((id) => id !== techId)
        : [...prev.members, techId],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="teams-page" className="p-6">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Teams
          </h1>
          <p className="text-slate-600">{filteredTeams.length} teams</p>
        </div>
        {canEdit && (
          <button
            data-testid="create-team-button"
            onClick={openCreateModal}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg shadow-sm transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Create Team
          </button>
        )}
      </div>

      {/* Search and View Toggle */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search teams..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              title="Grid View"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              title="List View"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeams.map((team) => (
            <div key={team.id} data-testid={`team-card-${team.id}`} className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {team.name}
                    </h3>
                    <p className="text-sm text-slate-600">{team.members?.length || 0} members</p>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditModal(team)}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit Team"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(team)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Team"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              {team.lead && (
                <div className="text-sm text-slate-600 mb-3">
                  <span className="font-medium">Lead:</span>{' '}
                  {formatMemberDisplay(team.lead)}
                </div>
              )}
              {/* Display team members as "Role - Name" */}
              {team.members && team.members.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Team Members</p>
                  <div className="space-y-1">
                    {team.members.slice(0, 5).map((memberId) => (
                      <div key={memberId} className="text-sm text-slate-700">
                        {formatMemberDisplay(memberId)}
                      </div>
                    ))}
                    {team.members.length > 5 && (
                      <div className="text-xs text-slate-500">+{team.members.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Team</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Members</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Lead</th>
                {canEdit && (
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTeams.map((team) => (
                <tr key={team.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="font-medium text-slate-900">{team.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {team.members?.slice(0, 3).map((memberId) => (
                        <div key={memberId} className="text-sm text-slate-600">
                          {formatMemberDisplay(memberId)}
                        </div>
                      ))}
                      {team.members?.length > 3 && (
                        <div className="text-xs text-slate-500">+{team.members.length - 3} more</div>
                      )}
                      {(!team.members || team.members.length === 0) && (
                        <span className="text-sm text-slate-400">No members</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {team.lead ? formatMemberDisplay(team.lead) : '-'}
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(team)}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Team"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(team)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Team"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl p-8 max-w-md w-full" data-testid="team-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {selectedTeam ? 'Edit Team' : 'Create New Team'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Team Name *</label>
                <input
                  data-testid="team-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                  placeholder="e.g., HVAC Team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Team Lead</label>
                <select
                  data-testid="team-lead-select"
                  value={formData.lead}
                  onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                >
                  <option value="">No lead</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.role ? `${member.role.charAt(0).toUpperCase() + member.role.slice(1)} - ` : ''}{member.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Members</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                  {teamMembers.map((member) => (
                    <label key={member.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        data-testid={`team-member-checkbox-${member.id}`}
                        type="checkbox"
                        checked={formData.members.includes(member.id)}
                        onChange={() => handleMemberToggle(member.id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">
                        {member.role ? `${member.role.charAt(0).toUpperCase() + member.role.slice(1)} - ` : ''}{member.name}
                      </span>
                    </label>
                  ))}
                  {teamMembers.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-2">No team members available. Add team members first.</p>
                  )}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  data-testid="submit-team-button"
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-colors"
                >
                  {selectedTeam ? 'Update Team' : 'Create Team'}
                </button>
                <button
                  data-testid="cancel-team-button"
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-xl p-8 max-w-md w-full">
            <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Delete Team
            </h2>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete the team "<span className="font-semibold">{selectedTeam.name}</span>"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete
              </button>
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedTeam(null); }}
                className="px-6 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;
